import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import client from 'lib/CommonApi/client';
import { AxiosError } from 'axios';
import { callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';
import { CardProductCode } from '../../../constants/Card';
import { SuccessResponseCodeList } from '../../../constants/Common';
import "./DeliveryInputConfirm.css";
import {Location} from 'history';

const DeliveryInputConfirm: React.FC = () => {
  const location = useLocation() as Location<LocationState>;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const screenId = "APPL27";
  const screenName = t("APPL27.title");

  const apply: any = useMemo(() => location.state.apply ?? {}, [location.state.apply]);

  const [isClick, setClick] = useState(false);
  const [displayCardIssueFee, setDisplayCardIssueFee] = useState<number | null>(null);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 표기 카드 발급비 조회 */
  const inquiryDisplayCardIssueFeeApi = useCallback(async () => {
    await client.get(`/api/app/apply/${apply.cardProductCode}/issueFee`, { params: location.state.query }).then((response) => {
      if (response.data.response) {
        setDisplayCardIssueFee(response.data.response.cardIssueFee);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    });
  }, [apply.cardProductCode, location]);

  /** Edit — 이전 화면(DeliveryInput)로 복귀. location.state.apply 통해 선택 상태가 그대로 복원된다 */
  const clickEdit = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** Confirm — 주소검증 → 주소 등록 → 다음 스텝 이동 */
  const clickConfirm = useCallback(async () => {
    if (isClick) return;
    setClick(true);
    showProgressWeb();

    // 1) 주소검증
    try {
      const verifyRes = await client.get("/api/app/apply/verify/address", {
        params: {
          ...location.state.query,
          homeZipCode: apply.homeZipCode,
          homeNewAddress: apply.homeNewAddress,
          homeNewDetailAddress: apply.homeNewDetailAddress,
        },
      });
      if (verifyRes.data.code !== "00") {
        alert(verifyRes.data.message);
        hideProgressWeb();
        setClick(false);
        return;
      }
    } catch (e: any) {
      callAlertWeb("ERROR", "99", e?.message ?? "");
      hideProgressWeb();
      setClick(false);
      return;
    }

    // 2) 카드신청내역 등록
    await client.patch("/api/app/apply/history", {
      ...location.state.query,
      receiveSeq: apply.receiveSeq,
      receiveDate: apply.receiveDate,
      receiveStatusCode: "07",
      sendTypeDivisionCode: apply.receiveMethod === "01" ? "1" : "3",
      deliverySelectCode: apply.deliverySelectCode,
      homeZipCode: apply.homeZipCode,
      homeNewAddress: apply.homeNewAddress,
      homeNewDetailAddress: apply.homeNewDetailAddress,
      homeEngAddress: apply.homeEngAddress,
      homeRoadManagementNo: apply.homeRoadManagementNo,
      thirdPartyReceiptYn: apply.thirdPartyReceiptYn,
      cardProductCode: apply.cardProductCode,
      addressChangeYn: apply.isChangeDelivery ? "Y" : "N",
      receiveMethod: apply.receiveMethod
    }).then(async (response) => {
      if (response.data.code === "00") {
        if (apply.isChangeDelivery) {
          navigate("/main", {
            state: {
              query: location.state.query,
              swiperIndex: location.state.swiperIndex,
              tabIndex: location.state.tabIndex
            }
          });
        } else {
          switch (Number(apply.cardProductCode)) {
            case CardProductCode.DreamCard:
            case CardProductCode.PremierCard:
              navigate("/apply/step7", {
                state: {
                  query: location.state.query,
                  apply: {
                    isChangeDelivery: false,
                    callPage: 'cardApply',
                    cardApply: apply,
                    ...apply
                  }
                }
              });
              break;
            case CardProductCode.WelcomeCard:
              if (apply.requestIssueDivisionCode?.startsWith("2") || apply.requestIssueDivisionCode?.startsWith("3")) {
                navigate("/apply/complet", {state: {...location.state, apply}});
              } else {
                await client.post("/api/app/apply/chargeCardIssueFee", {
                  ...location.state.query,
                  receiveSeq: apply.receiveSeq,
                  receiveDate: apply.receiveDate
                }).then((res) => {
                  if (SuccessResponseCodeList.includes(res.data.code)) {
                    if (res.data.response.bCharged === true) {
                      navigate("/apply/complet", {state: {...location.state, apply}});
                    } else {
                      navigate("/apply/depositCardIssuanceFee", {
                        state: {
                          ...location.state,
                          apply,
                          storage: {
                            ...location.state.storage,
                            chargeCardIssueFeeRes: res.data.response,
                            displayCardIssueFee: displayCardIssueFee
                          }
                        }
                      });
                    }
                  } else {
                    callAlertWeb("ERROR", res.data.code, res.data.message);
                  }
                }).catch((e: AxiosError) => {
                  callAlertWeb("ERROR", "99", e.message);
                });
              }
              break;
            case CardProductCode.DirectCard:
            default:
              navigate("/apply/complet", {state: {...location.state, apply}});
              break;
          }
        }
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      setClick(false);
    });
  }, [isClick, location, navigate, apply, displayCardIssueFee]);

  useEffect(() => {
    i18n.changeLanguage(location.state.query.languageCode);
    callNativeWeb(screenId, screenName);
    footerShowEventWeb("N");

    inquiryDisplayCardIssueFeeApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  const detail = apply.homeNewDetailAddress ?? "";
  const engFull = [apply.homeEngAddress, detail].filter(Boolean).join(" ");
  const korFull = [apply.homeNewAddress, detail].filter(Boolean).join(" ");

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <h2 className="confirmTitle">{t(screenId + ".header01", "Please confirm the card receiving address")}</h2>

            <dl className="addressConfirmList">
              <dt>{t(screenId + ".header02", "Zip Code")}</dt>
              <dd>{apply.homeZipCode}</dd>

              <dt>{t(screenId + ".header03", "Address")}</dt>
              <dd>
                <span className="langTag">({t(screenId + ".header04", "Eng")})</span>
                <span className="addrText">{engFull}</span>
              </dd>
              <dd>
                <span className="langTag">({t(screenId + ".header05", "Kor")})</span>
                <span className="addrText">{korFull}</span>
              </dd>
            </dl>

            <div className="editBtnWrap">
              <button type="button" className="editBtn" onClick={clickEdit}>{t(screenId + ".button01", "Edit")}</button>
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={isClick} onClick={clickConfirm}>
              <span>{t("COMMON.button.confirm", "Confirm")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryInputConfirm;